/**
 * What the bell draws for one notification. Meeting and model rows are
 * described as data so the view can render them with Lingui. The model id is
 * only included when the caller is the platform bell.
 */

import type { INotificationLike } from "./dedupeNotifications"
import { meetingIdOf, readNotificationMetadata } from "./dedupeNotifications"

export type TNotificationTitleMode = "stored" | "meeting" | "model"

export type TNotificationLink = "meeting" | "try-again" | "settings"

export type TNotificationBody =
  | { kind: "counts"; actionCount: number; decisionCount: number }
  | { kind: "transcript" }
  | { kind: "reason"; reason: string | null }
  | { kind: "ready" }
  | { kind: "model" }
  | { kind: "text"; text: string }

export interface IDescribedNotification {
  titleMode: TNotificationTitleMode
  subject: string | null
  lite: boolean
  startedAt: string | null
  body: TNotificationBody
  meetingId: string | null
  link: TNotificationLink | null
  /** Set only when the platform bell is allowed to show it. */
  modelId: string | null
}

export interface IDescribeOptions {
  showModelIds: boolean
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null
}

function readCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0
}

function readBool(value: unknown): boolean {
  return value === true
}

function legacyPlaceholder(notification: INotificationLike): boolean {
  return notification.message.includes('"Your meeting"') || notification.message.includes('"A meeting"')
}

function modelIdFor(notification: INotificationLike, showModelIds: boolean): string | null {
  if (!showModelIds) return null
  const fromMetadata = readString(readNotificationMetadata(notification.metadata)?.modelId)
  if (fromMetadata) return fromMetadata
  return readString(notification.adapter)
}

function meetingDescription(notification: INotificationLike): IDescribedNotification {
  const meta = readNotificationMetadata(notification.metadata)
  const meetingId = meetingIdOf(notification)
  const failed = notification.type === "meeting.failed"
  const link: TNotificationLink | null = meetingId ? (failed ? "try-again" : "meeting") : null
  const structured = meta?.kind === "meeting.processed" || meta?.kind === "meeting.failed"
  if (!structured) {
    if (legacyPlaceholder(notification)) {
      return {
        titleMode: "meeting",
        subject: null,
        lite: false,
        startedAt: null,
        body: failed ? { kind: "reason", reason: null } : { kind: "ready" },
        meetingId,
        link,
        modelId: null,
      }
    }
    return {
      titleMode: "stored",
      subject: null,
      lite: false,
      startedAt: null,
      body: { kind: "text", text: notification.message },
      meetingId,
      link,
      modelId: null,
    }
  }
  const lite = readBool(meta?.lite)
  const subject = readString(meta?.subject)
  const startedAt = readString(meta?.startedAt)
  let body: TNotificationBody
  if (failed) {
    body = { kind: "reason", reason: readString(meta?.reason) }
  } else if (lite || meta?.transcriptReady === true) {
    body = { kind: "transcript" }
  } else {
    body = {
      kind: "counts",
      actionCount: readCount(meta?.actionCount),
      decisionCount: readCount(meta?.decisionCount),
    }
  }
  return {
    titleMode: "meeting",
    subject,
    lite,
    startedAt,
    body,
    meetingId,
    link,
    modelId: null,
  }
}

export function describeNotification(
  notification: INotificationLike,
  options: IDescribeOptions,
): IDescribedNotification {
  if (notification.type === "model_deprecated") {
    return {
      titleMode: "model",
      subject: null,
      lite: false,
      startedAt: null,
      body: { kind: "model" },
      meetingId: null,
      link: "settings",
      modelId: modelIdFor(notification, options.showModelIds),
    }
  }
  if (notification.type === "meeting.processed" || notification.type === "meeting.failed") {
    return meetingDescription(notification)
  }
  const meetingId = meetingIdOf(notification)
  return {
    titleMode: "stored",
    subject: null,
    lite: false,
    startedAt: null,
    body: { kind: "text", text: notification.message },
    meetingId,
    link: meetingId ? "meeting" : null,
    modelId: null,
  }
}
